// Italian VAT ID (Partita IVA / PIVA) validation.
//
// A PIVA is 11 numeric digits validated with a Luhn-style checksum.
// We accept an optional "IT" country prefix (case-insensitive) and validate
// the 11 digits that follow, since Italian VAT IDs are written both ways:
//   "12345678901"    (local Partita IVA)
//   "IT12345678901"  (EU / international format)

/** Strip an optional leading IT country code and surrounding whitespace. */
export function normalizePartitaIVA(value: string): string {
  if (typeof value !== "string") return "";
  return value.trim().replace(/^IT/i, "");
}

/**
 * Validate the formal correctness of an Italian Partita IVA.
 * Returns true only when the value is exactly 11 digits and its checksum is valid.
 */
export function isValidPartitaIVA(value: string): boolean {
  if (typeof value !== "string") return false;

  const piva = normalizePartitaIVA(value);

  // Format: exactly 11 digits
  if (!/^[0-9]{11}$/.test(piva)) return false;

  let somma = 0;
  for (let i = 0; i < 11; i++) {
    let n = parseInt(piva[i], 10);
    if (i % 2 === 1) {
      // even position (odd index) — double and cast out nines
      n *= 2;
      if (n > 9) n -= 9;
    }
    somma += n;
  }

  return somma % 10 === 0;
}
